<template>
  <div class="order-actions-bar">
    <!-- TIER 1 — always visible (AB#21204). Order matters: this is the
         sequence the row reads in, and it should not be reshuffled without
         a reason from the Crazy Egg numbers. -->
    <!-- mark dqc -->
    <label
      v-if="can('read-dqc-status') || can('write-dqc-status')"
      class="order-action-btn"
      :class="[
        (order_item as DashboardItem).order.mark_dqc
          ? 'bg-green-100 hover:bg-green-200 border-green-500 text-green-600'
          : 'bg-white border-gray-300 text-gray-700',
        can('write-dqc-status') ? 'cursor-pointer' : 'cursor-not-allowed',
      ]"
      :title="
        (order_item as DashboardItem).order.mark_dqc &&
        (order_item as DashboardItem).order.dqc_user_name
          ? `DQC (${(order_item as DashboardItem).order.dqc_user_name}) approved on ` +
            formatTimestampWithBrowserTimezone((order_item as DashboardItem).order.dqc_marked_at)
          : (order_item as DashboardItem).order.mark_dqc
            ? `DQC approved on ` +
              formatTimestampWithBrowserTimezone((order_item as DashboardItem).order.dqc_marked_at)
            : can('write-dqc-status')
              ? 'Mark as DQC'
              : 'View only (no permission to modify)'
      "
    >
      <input
        type="checkbox"
        :checked="(order_item as DashboardItem).order.mark_dqc"
        :disabled="!can('write-dqc-status')"
        @change="
          can('write-dqc-status') && toggleDQC((order_item as DashboardItem).order.id, $event)
        "
        class="custom-dqc-checkbox w-4 h-4 rounded-md appearance-none border-2 border-gray-300 transition-all duration-150 relative"
        :class="[can('write-dqc-status') ? 'cursor-pointer' : 'cursor-not-allowed']"
      />
      <span :class="[can('write-dqc-status') ? 'cursor-pointer' : 'cursor-not-allowed']">DQC</span>
    </label>
    <!-- Assign Urgency -->
    <Dropdown
      v-if="can('update-order-urgency')"
      triggerStyle="order-action-btn  h-full"
      :customClass="'urgency-dropdown'"
    >
      <template #trigger>
        <button class="h-full order-action-btn urgency-trigger" type="button">
          <span>Assign Urgency</span>
          <ChevronRightIcon class="size-4 text-[#686666] ml-auto" />
        </button>
      </template>
      <template #contents>
        <div class="urgency-list">
          <button
            @click="updateUrgency('vip', (order_item as DashboardItem).order.id)"
            class="urgency-option vip"
            type="button"
          >
            <span
              class="rounded-full bg-green-300 group-hover:bg-green-400 px-[10px] py-[10px]"
            ></span>
            VIP
          </button>
          <button
            @click="updateUrgency('urgent', (order_item as DashboardItem).order.id)"
            class="urgency-option urgent"
            type="button"
          >
            <span
              class="rounded-full order_cancel group-hover:bg-red-300 px-[10px] py-[10px]"
            ></span>
            Urgent
          </button>
          <button
            @click="updateUrgency('extra_attention', (order_item as DashboardItem).order.id)"
            class="urgency-option extra"
            type="button"
          >
            <span
              class="rounded-full bg-blue-300 group-hover:bg-blue-400 px-[10px] py-[10px]"
            ></span>
            Extra Attention
          </button>
          <button
            @click="updateUrgency(null, (order_item as DashboardItem).order.id)"
            class="urgency-option remove"
            type="button"
          >
            <span
              class="rounded-full bg-gray-300 group-hover:bg-gray-400 px-[10px] py-[10px]"
            ></span>
            Remove Urgency
          </button>
        </div>
      </template>
    </Dropdown>
    <a
      :href="`${appURL}/edit-techpack/${order?.id}?source=new_dashboard`"
      class="order-action-btn"
      v-if="can('update-techpack') && !IsCustomTechpackUploaded"
    >
      Edit Techpack
    </a>
    <button
      class="order-action-btn"
      @click="copyOrder(order_item.order.id)"
      v-if="can('copy-order')"
    >
      Copy Order
    </button>
    <!-- Put Order On Hold / Resume -->
    <button
      v-if="order_item?.status !== 'on_hold' && can('put-order-on-hold')"
      class="order-action-btn"
      @click="openPutOnHoldModal('put')"
      title="Put Order on Hold"
    >
      Put Order on Hold
    </button>
    <button
      v-else-if="order_item?.status == 'on_hold' && can('put-order-on-hold')"
      class="flex gap-1 items-center justify-center text-sm px-2 py-2 h-max font-medium text-[#00B087] border-[1px] border-[#00B087] bg-white rounded-[5px] whitespace-nowrap"
      @click="openPutOnHoldModal('resume')"
      title="Resume Order"
    >
      Resume Order
    </button>
    <button
      v-if="can('add-internal-order-comment')"
      class="order-action-btn"
      @click="() => (internalNoteModal = true)"
    >
      Add Internal Comment
    </button>
    <button
      class="order-action-btn"
      @click="() => handleOrderPriceModal()"
      v-if="can('view-price-breakdown')"
    >
      Order Price
      {{
        `${getOrderItemNetPriceCurrencyCode(orderItemForPriceDisplay)} ` +
        `${getOrderItemPriceInfoDisplayAmount(orderItemForPriceDisplay?.price_info).toFixed(2)}`
      }}
    </button>
    <!-- Support Threads — the customer and factory conversations in Zendesk
         (AB#21116, retiered and renamed in AB#21204).

         Renamed from "Zendesk": it opens DDI's helpdesk from an Operations
         screen, so it carries neither agent's name. "ELSA Conversations" would
         have collided with Ask ELSA and read as the one thing that talks to
         ELSA, which is the one thing it does not.

         Rendered for EVERY company, and aria-disabled on the ones without
         Zendesk rather than hidden. Two reasons, and the second is the one that
         matters: the row stops reshuffling between orders, and a control that
         is present but inert has to say why. `aria-disabled` rather than
         `disabled` keeps it in the tab order and audible to a screen reader —
         a `disabled` button that silently swallows clicks is how dead clicks
         are manufactured, which is the thing this ticket exists to reduce. -->
    <button
      class="order-action-btn"
      data-test="order-zendesk"
      :aria-disabled="hasSupportThreads ? undefined : 'true'"
      :class="{ 'order-action-btn--unavailable': !hasSupportThreads }"
      @click="hasSupportThreads && (zendeskModal = true)"
      :title="
        hasSupportThreads
          ? 'Customer and factory conversations for this order'
          : 'Support threads are available for Hummel Pro NA orders'
      "
    >
      Support Threads
    </button>

    <!-- TIER 2 — everything else, behind one menu (AB#21204).

         `menu` opts the shared Dropdown into role="menu", arrow keys, Escape
         and focus return. Moving half the row out of sight only works if all
         of it is still reachable. -->
    <Dropdown
      menu
      triggerAriaLabel="More actions"
      triggerStyle="order-action-btn order-actions-more-trigger"
      :customClass="'order-actions-more'"
    >
      <template #trigger>
        More
        <EllipsisHorizontalIcon class="size-4 ml-1 inline align-text-bottom" />
      </template>
      <template #contents>
        <div class="order-actions-menu">
          <!-- Edit Factory -->
          <button
            role="menuitem"
            data-test="menu-edit-factory"
            v-if="can('change-factory') && (order_item as DashboardItem).factory_id !== null"
            class="order-action-btn"
            @click="
              changeFactory(
                (order_item as DashboardItem).order.id,
                (order_item as DashboardItem).id
              )
            "
            title="Edit Factory"
          >
            Edit Factory
          </button>
          <!-- Delivery Request -->
          <button
            role="menuitem"
            data-test="menu-request-delivery"
            v-if="can('request-delivery-date') && (order_item as DashboardItem).factory_id !== null"
            class="order-action-btn"
            @click="() => handleEnterDeliveryRequestDate(order_item)"
          >
            Request Delivery By
          </button>
          <!-- Notify Customer -->
          <button
            role="menuitem"
            data-test="menu-notify-customer"
            v-if="
              currentTab == 'quote_requested_items' &&
              (order_item as DashboardItem).status == 'quote_provided'
            "
            class="order-action-btn"
            @click="notifyCustomerForQuote((order_item as DashboardItem).order_id)"
            title="Notify Customer"
          >
            Notify Customer
          </button>
          <!-- Provide Quote -->
          <button
            role="menuitem"
            data-test="menu-provide-quote"
            v-if="
              currentTab == 'quote_requested_items' &&
              (order_item as DashboardItem).status == 'quote_requested' &&
              (order_item as DashboardItem).order.admin_salesrep_id == null
            "
            class="order-action-btn"
            @click="provideQuoteModal = true"
            title="Provide Quote"
          >
            Provide Quote
          </button>
          <!-- Tech Pack PDF -->
          <div v-if="viewDefaultTechpacks">
            <template v-if="app_environment === 'Production'">
              <template v-if="(order_item as DashboardItem).order?.id > 115">
                <Dropdown
                  v-if="(order_item as DashboardItem).techpack_data_count > 0"
                  triggerStyle="order-action-btn"
                  :customClass="'techpack-dropdown'"
                >
                  <template #trigger>
                    <button
                      class="order-action-btn techpack-trigger"
                      @click.stop="fetchAndOpenPdf((order_item as DashboardItem).order, 0)"
                      type="button"
                    >
                      Techpack PDF
                      <ChevronRightIcon class="size-4 text-[#686666] ml-auto" />
                    </button>
                  </template>
                  <template #contents>
                    <div class="techpack-list">
                      <button
                        v-for="techpackDataVersion in order_item.techpack_data_count"
                        :key="`order-item-version-${techpackDataVersion}`"
                        @click="
                          fetchAndOpenPdf(
                            (order_item as DashboardItem).order,
                            techpackDataVersion + 1
                          )
                        "
                        class="techpack-option"
                        type="button"
                      >
                        Version {{ techpackDataVersion + 1 }}
                      </button>
                    </div>
                  </template>
                </Dropdown>
                <button
                  v-else
                  class="order-action-btn techpack-trigger"
                  @click="fetchAndOpenPdf((order_item as DashboardItem).order, 0)"
                  type="button"
                >
                  Techpack PDF
                </button>
              </template>
            </template>
            <template v-else>
              <div
                class="relative flex items-center overflow-hidden order-action-btn techpack-trigger"
              >
                <button
                  class="flex items-center justify-center w-full h-full"
                  @click="fetchAndOpenPdf((order_item as DashboardItem).order, 0)"
                  type="button"
                >
                  <span>Techpack</span>
                </button>

                <Dropdown
                  v-if="(order_item as DashboardItem).techpack_data_count > 0"
                  triggerStyle=""
                  :customClass="'flex'"
                >
                  <template #trigger>
                    <button type="button" class="flex items-center justify-center h-full">
                      <ChevronRightIcon class="size-4 text-[#686666]" />
                    </button>
                  </template>

                  <template #contents>
                    <div class="techpack-list">
                      <button
                        v-for="techpackDataVersion in order_item.techpack_data_count"
                        :key="`order-item-version-${techpackDataVersion}`"
                        @click="
                          fetchAndOpenPdf((order_item as DashboardItem).order, techpackDataVersion)
                        "
                        class="techpack-option"
                        type="button"
                      >
                        Version {{ techpackDataVersion }}
                      </button>
                    </div>
                  </template>
                </Dropdown>
              </div>
            </template>
          </div>
          <div v-if="viewMerchatTechpacks" class="order-action-btn techpack-action-btn">
            <template v-if="app_environment === 'Production'">
              <template v-if="(order_item as DashboardItem).order?.id > 115">
                <button
                  @click="
                    onDownloadClick(
                      merchant_techpack_urls[0],
                      `${storage_url}/${merchant_techpack_urls[0]}`,
                      `merchant_${(order_item as DashboardItem).order_no ?? (order_item as DashboardItem).order_id}_techpack_v0.pdf`
                    )
                  "
                  class="flex items-center justify-between w-full gap-2"
                  :class="{
                    'pt-0 pb-0 pr-0': merchant_techpack_urls.length === 0,
                  }"
                >
                  Merchant Techpack
                </button>
                <Dropdown v-if="merchant_techpack_urls.length > 1" triggerStyle="rounded-lg pt-2">
                  <template #trigger>
                    <ChevronRightIcon class="text-[#686666] size-4" />
                  </template>

                  <template #contents>
                    <ul>
                      <template
                        v-if="merchant_techpack_urls.length > 1"
                        :key="`order-item-version-${techpackDataVersion}`"
                      >
                        <li
                          v-for="(
                            techpackDataVersion, techpackIndex
                          ) in merchant_techpack_urls.slice(1)"
                          @click="
                            onDownloadClick(
                              techpackDataVersion,
                              `${storage_url}/${techpackDataVersion}`,
                              `merchant_${(order_item as DashboardItem).order_no ?? (order_item as DashboardItem).order_id}_techpack_v${techpackIndex + 1}.pdf`
                            )
                          "
                          class="px-2 py-2 text-[#292D32] cursor-pointer duration-300 hover:bg-[#F4F4F4] flex items-center justify-start gap-2"
                        >
                          Version {{ techpackIndex + 1 }}
                        </li>
                      </template>
                    </ul>
                  </template>
                </Dropdown>
              </template>
            </template>
            <template v-else>
              <button
                class="flex justify-between w-full"
                :class="{
                  'pt-0 pb-0 pr-0': merchant_techpack_urls.length === 0,
                }"
                @click="
                  onDownloadClick(
                    merchant_techpack_urls[0],
                    `${storage_url}/${merchant_techpack_urls[0]}`,
                    `merchant_${(order_item as DashboardItem).order_no ?? (order_item as DashboardItem).order_id}_techpack_v0.pdf`
                  )
                "
              >
                Merchant Techpack
              </button>
              <Dropdown v-if="merchant_techpack_urls.length > 1" triggerStyle="rounded-lg pt-2">
                <template #trigger>
                  <ChevronRightIcon class="text-[#686666] size-4" />
                </template>

                <template #contents>
                  <ul>
                    <template
                      v-if="merchant_techpack_urls.length > 1"
                      :key="`order-item-version-${techpackDataVersion}`"
                    >
                      <li
                        v-for="(techpackDataVersion, techpackIndex) in merchant_techpack_urls.slice(
                          1
                        )"
                        @click="
                          onDownloadClick(
                            techpackDataVersion,
                            `${storage_url}/${techpackDataVersion}`,
                            `merchant_${(order_item as DashboardItem).order_no ?? (order_item as DashboardItem).order_id}_techpack_v${techpackIndex + 1}.pdf`
                          )
                        "
                        class="px-2 py-2 text-[#292D32] cursor-pointer duration-300 hover:bg-[#F4F4F4] flex items-center justify-start gap-2"
                      >
                        Version {{ techpackIndex + 1 }}
                      </li>
                    </template>
                  </ul>
                </template>
              </Dropdown>
            </template>
          </div>
          <!-- Factory PDF -->
          <div v-if="viewFactoryTechpacks" class="order-action-btn techpack-action-btn">
            <template v-if="app_environment === 'Production'">
              <template v-if="(order_item as DashboardItem).order?.id > 115">
                <!-- <li class="flex justify-between w-full"> -->
                <button
                  class="flex items-center justify-between w-full"
                  :class="{
                    'pt-0 pb-0 pr-0': factory_techpack_urls.length === 0,
                  }"
                  @click="
                    onDownloadClick(
                      factory_techpack_urls[0],
                      `${storage_url}/${factory_techpack_urls[0]}`,
                      `factory_${(order_item as DashboardItem).order_no ?? (order_item as DashboardItem).order_id}_techpack_v0.pdf`
                    )
                  "
                >
                  Factory Techpack
                  <Dropdown v-if="factory_techpack_urls.length > 1" triggerStyle="rounded-lg pt-2">
                    <template #trigger>
                      <ChevronRightIcon class="text-[#686666] size-4" />
                    </template>

                    <template #contents>
                      <ul>
                        <template
                          v-for="(
                            techpackDataVersion, techpackIndex
                          ) in factory_techpack_urls.slice(1)"
                          :key="`order-item-version-${techpackIndex}`"
                        >
                          <li
                            @click="
                              onDownloadClick(
                                techpackDataVersion,
                                `${storage_url}/${techpackDataVersion}`,
                                `factory_${(order_item as DashboardItem).order_no ?? (order_item as DashboardItem).order_id}_techpack_v${techpackIndex + 1}.pdf`
                              )
                            "
                            class="px-2 py-2 text-[#292D32] cursor-pointer duration-300 hover:bg-[#F4F4F4] flex items-center justify-start gap-2"
                          >
                            Version {{ techpackIndex + 1 }}
                          </li>
                        </template>
                      </ul>
                    </template>
                  </Dropdown>
                </button>
                <!-- </li> -->
              </template>
            </template>
            <template v-else>
              <button
                class="flex items-center justify-between w-full gap-2"
                :class="{
                  'pt-0 pb-0 pr-0': factory_techpack_urls.length === 0,
                }"
                @click="
                  onDownloadClick(
                    factory_techpack_urls[0],
                    `${storage_url}/${factory_techpack_urls[0]}`,
                    `factory_${(order_item as DashboardItem).order_no ?? (order_item as DashboardItem).order_id}_techpack_v0.pdf`
                  )
                "
              >
                <span>Factory Techpack</span>
              </button>
              <Dropdown triggerStyle="pt-2" v-if="factory_techpack_urls.length > 1">
                <template #trigger>
                  <ChevronRightIcon class="text-[#686666] size-4" />
                </template>
                <template #contents>
                  <ul>
                    <template
                      v-if="factory_techpack_urls.length > 0"
                      :key="`order-item-version-${techpackDataVersion}`"
                    >
                      <li
                        v-for="(techpackDataVersion, techpackIndex) in factory_techpack_urls.slice(
                          1
                        )"
                        @click="
                          onDownloadClick(
                            techpackDataVersion,
                            `${storage_url}/${techpackDataVersion}`,
                            `factory_${(order_item as DashboardItem).order_no ?? (order_item as DashboardItem).order_id}_techpack_v${techpackIndex + 1}.pdf`
                          )
                        "
                        class="px-2 py-2 text-[#292D32] cursor-pointer duration-300 hover:bg-[#F4F4F4] flex items-center justify-start gap-2"
                      >
                        Version {{ techpackIndex + 1 }}
                      </li>
                    </template>
                  </ul>
                </template>
              </Dropdown>
            </template>
          </div>
          <button
            role="menuitem"
            data-test="menu-upload-techpack"
            v-if="can('update-custom-techpack')"
            class="order-action-btn"
            @click="customTechpackModal = true"
            title="Provide Quote"
          >
            <template v-if="!IsCustomTechpackUploaded">Upload Custom Techpack</template>
            <template v-else>View Custom Techpack</template>
          </button>
          <button
            role="menuitem"
            data-test="menu-factory-pi"
            v-if="can('download-factory-pi') && (order_item as DashboardItem).order.factory_pi_url"
            class="order-action-btn"
            @click="downloadFactoryPI((order_item as DashboardItem).order)"
            title="Download Factory PI"
          >
            Factory PI
          </button>
          <!-- Generate Barcode -->
          <button
            role="menuitem"
            data-test="menu-generate-barcode"
            v-if="can('generate-barcodes')"
            class="order-action-btn"
            @click="generateBarcode"
            title="Generate Barcode"
          >
            Generate Barcode
          </button>
          <!-- Generate QR code (staging-only; no permission gate for now) -->
          <button
            role="menuitem"
            data-test="menu-generate-qr-code"
            class="order-action-btn"
            @click="generateQrCode"
            title="Generate QR code"
          >
            Generate QR code
          </button>
          <button
            role="menuitem"
            data-test="menu-download-confirmation"
            class="order-action-btn"
            @click="handleDownloadConfirmation((order_item as DashboardItem).order.order_no)"
            title="Download Order Confirmation"
            :disabled="isDownloading"
          >
            <span class="ml-1 text-xs text-gray-500" v-if="isDownloading">Downloading...</span>
            <span v-else>Download Order Confirmation</span>
          </button>
          <!-- Delete Order last, alone, below a rule. Nothing routine should sit
               beside the one action that cannot be undone. -->
          <hr class="order-actions-menu-rule" />
          <!-- Delete Order -->
          <button
            role="menuitem"
            data-test="menu-delete-order"
            v-if="can('delete-order')"
            class="order-action-btn delete-order-btn"
            @click="
              deleteOrder(
                (order_item as DashboardItem).order.id,
                (order_item as DashboardItem).order_no ?? (order_item as DashboardItem).order.id
              )
            "
            title="Delete Order"
          >
            Delete Order
          </button>
        </div>
      </template>
    </Dropdown>
  </div>

  <!-- Modals -->

  <Teleport to="body">
    <Transition name="fade">
      <ProvideQuoteModal
        @onQuoteProvided="onFetch(true)"
        v-if="provideQuoteModal"
        :row="order_item"
      />
    </Transition>

    <Transition name="fade">
      <ChangeFactoryDialog
        :currentTab="currentTab"
        @close="onFetch"
        v-if="changeFactoryModal"
        :order_item="order_item"
      />
    </Transition>

    <!-- <PriceBreakdownModal
    :order="order_item.order"
    :appUrl="appURL"
    :can="can"


    :order="(order_item as DashboardItem).order"
    v-if="priceBreakdownModal"
    @close="handlePriceBreakDownModalCloseEvent"
  /> -->
    <!-- <CustomTechPackModal
    :order="(order_item as DashboardItem).order"
    :customTechpackModal="customTechpackModal"
    v-if="customTechpackModal"
    @close="() => (customTechpackModal = false)"
  /> -->

    <Transition name="fade">
      <PutOrderOnHoldModal
        v-if="showPutOnHoldModal"
        :orderItemId="putModalOrderItemId"
        :mode="putModalMode"
        :title="putModalTitle"
        @close="() => (showPutOnHoldModal = false)"
        @success="handlePutOnHoldSuccess"
      />
    </Transition>
    <Transition name="fade">
      <AddInternalNoteModal
        :order_item_id="(order_item as DashboardItem).id"
        :internal_comment="
          order_item.internal_comment ? JSON.parse(order_item.internal_comment) : null
        "
        v-if="internalNoteModal"
        @on-close="handleCloseInternalNoteModal"
        @on-cancel="() => (internalNoteModal = false)"
      />
    </Transition>
    <Transition name="fade">
      <CustomTechPackModal
        v-if="customTechpackModal"
        :order="(order_item as DashboardItem).order"
        @close="handleCustomTechPackModalCloseEvent"
        @onFetch="() => emits('handleOrderUpdated')"
      />
    </Transition>

    <Transition name="fade">
      <CopyOrderModal
        v-if="openCopyModal"
        :order="fullOrderDetails"
        @close="() => (openCopyModal = false)"
        @save="onFetch"
      />
    </Transition>

    <Transition name="fade">
      <BarcodeGenerationModal
        v-if="showBarcodeModal"
        :show="showBarcodeModal"
        :order_no="order_item.order.order_no"
        @close="showBarcodeModal = false"
      />
    </Transition>

    <Transition name="fade">
      <QrCodeGenerationModal
        v-if="showQrModal"
        :show="showQrModal"
        :order_no="order_item.order.order_no"
        @close="showQrModal = false"
      />
    </Transition>

    <Transition name="fade">
      <DeliveryRequestModal
        v-if="deliveryRequestDate"
        :delivery_request_date="delivery_request_date"
        :is_exists_request_delivery_date="isExistsDeliveryRequestDate"
        :orderItemId="(order_item as DashboardItem)?.id"
        @close="deliveryRequestDate = false"
        @success="handleDeliveryRequest"
      />
    </Transition>
    <ZendeskTicketsModal
      v-if="zendeskModal"
      :orderId="(order_item as DashboardItem).order.id"
      :orderNo="(order_item as DashboardItem).order.order_no"
      @close="zendeskModal = false"
    />
  </Teleport>
</template>

<script setup lang="ts">
import Dropdown from '@/components/Shared/Dropdown.vue';
import { can, downloadFile, logError, openV2Application } from '@/composables';
import { showToast } from '@/composables/swal';
import { http, handleResponseException } from '@/interceptors/axiosRequestInterceptor';
import { DashboardItem, OrderDetail } from '@/models';
import {
  getOrderItemNetPriceCurrencyCode,
  getOrderItemPriceInfoDisplayAmount,
} from '@/utils/orderItemPriceDisplay';
import { useMainStore } from '@/store';
import { ChevronRightIcon, EllipsisHorizontalIcon } from '@heroicons/vue/24/solid';
import { storeToRefs } from 'pinia';
import { computed, ComputedRef, onMounted, ref, watch } from 'vue';
import AddInternalNoteModal from '../../Modals/AddInternalNoteModal.vue';
import DeliveryRequestModal from '../../Modals/DeliveryRequestModal.vue';

import ChangeFactoryDialog from '../../Modals/ChangeFactoryModal.vue';
import CopyOrderModal from '../../Modals/CopyOrderModal.vue';
import CustomTechPackModal from '../../Modals/CustomTechPackModal.vue';
import ProvideQuoteModal from '../../Modals/ProvideQuoteModal.vue';

import { formatTimestampWithBrowserTimezone } from '@/composables/useTimeZoneFormatDate';

import PutOrderOnHoldModal from '../../Modals/PutOrderOnHoldModal.vue';
import BarcodeGenerationModal from '@/components/Shared/BarcodeGenerationModal.vue';
import QrCodeGenerationModal from '@/components/Shared/QrCodeGenerationModal.vue';
import ZendeskTicketsModal from '../../Modals/ZendeskTicketsModal.vue';
import { isZendeskCompany } from '@/composables/useZendeskTickets';

const storage_url = import.meta.env.VITE_APP_FILE_URL;
const app_environment = import.meta.env.VITE_APP_NODE_ENV;
const changeFactoryModal = ref<boolean>(false);
const provideQuoteModal = ref<boolean>(false);
const priceBreakdownModal = ref<boolean>(false);
const customTechpackModal = ref<boolean>(false);
const internalNoteModal = ref<boolean>(false);

const openCopyModal = ref<boolean>(false);
const fullOrderDetails = ref<OrderDetail | null>(null);

const orderItemStatus = ref<string>('');
const resumeLoading = ref(false);

const showPutOnHoldModal = ref<boolean>(false);
const putModalMode = ref<'put' | 'resume'>('put');
const putModalTitle = ref<string | undefined>(undefined);
const putModalOrderItemId = ref<number | string | null>(null);
const delivery_request_date = ref<Date | string | null>(null);
const isExistsDeliveryRequestDate = ref<boolean>(false);
const deliveryRequestDate = ref<Boolean>(false);

const showBarcodeModal = ref<boolean>(false);
const showQrModal = ref<boolean>(false);
const zendeskModal = ref<boolean>(false);

/**
 * Whether this order's company has Zendesk conversations (AB#21204).
 *
 * Kept as a computed rather than inlined in three places in the template: the
 * button reads it to decide whether it is live, to pick its tooltip, and to
 * decide whether the click does anything. Three copies of the same condition
 * drift.
 */
const hasSupportThreads: ComputedRef<boolean> = computed(() =>
  isZendeskCompany((order_item.value as DashboardItem)?.order?.company_id)
);

/**
 * Two of these actions can be started from somewhere else on the page.
 *
 * Ask ELSA's read card ends in "do this next", and a recommendation that then
 * asks the reader to go and find the right button themselves is most of a
 * recommendation. These let it open the same modal this row opens — the modal,
 * its permissions and its save path all stay here, so there is one
 * implementation and one place a rule can change.
 *
 * Both are no-ops when the underlying control would not be offered. A caller
 * cannot open a modal this row would not have let the user open; supportThreads
 * additionally reports back whether it did anything, so a card can hide the
 * button rather than offering a dead one.
 */
const openInternalNote = (): boolean => {
  if (!can('add-internal-order-comment')) {
    return false;
  }

  internalNoteModal.value = true;

  return true;
};

const openSupportThreads = (): boolean => {
  if (!hasSupportThreads.value) {
    return false;
  }

  zendeskModal.value = true;

  return true;
};

const canOpenInternalNote = computed<boolean>(() => Boolean(can('add-internal-order-comment')));

defineExpose({
  openInternalNote,
  openSupportThreads,
  canOpenInternalNote,
  hasSupportThreads,
});

const emits = defineEmits(['onFetch', 'openOrderPriceModal', 'handleOrderUpdated']);

const props = defineProps<{
  order_item_list: any;
  currentTab: string;
  order: OrderDetail;
  order_item_price?: number;
}>();

const { order_item_list, currentTab } = props;
const { loading, user: currentUser } = storeToRefs(useMainStore());
const appURL = import.meta.env.VITE_VUE2_APP_URL;
const order_item = ref<DashboardItem>({ ...order_item_list });

const {
  deleteOrder: DeleteFromStore,
  notifyCustomerForQuote,
  updateOrderUrgency,
  getOrderDetail,
  updateOrderDQCStatus,
} = useMainStore();

type Urgency = 'vip' | 'urgent' | 'extra_attention';

const isDownloading = ref(false);

/** Merge dashboard list row with order-detail API (price_info + currency from detail after price breakdown save). */
const orderItemForPriceDisplay = computed((): DashboardItem => {
  const row = order_item.value as DashboardItem;
  const detailItem = props.order?.items?.find((i) => i.id === row.id);

  return {
    ...row,
    price_info: detailItem?.price_info ?? row.price_info,
    currency: detailItem?.currency ?? row.currency,
    billing_company_id: row.billing_company_id ?? detailItem?.billing_company_id,
    billing_company: detailItem?.billing_company ?? (row as any).billing_company,
    billingCompany: detailItem?.billing_company ?? (row as any).billingCompany,
    merchant_currency: row.merchant_currency ?? (detailItem as any)?.merchant_currency,
    price_list_detail: row.price_list_detail ?? (detailItem as any)?.price_list_detail,
    item_invoices:
      (detailItem as any)?.item_invoices ??
      (detailItem as any)?.itemInvoices ??
      row.item_invoices ??
      row.itemInvoices,
    invoice_composite_status: detailItem?.invoice_composite_status ?? row.invoice_composite_status,
    order: {
      ...row.order,
      company: props.order?.company ?? row.order?.company,
      price_info: props.order?.price_info ?? row.order?.price_info,
      additional_fields: props.order?.additional_fields ?? row.order?.additional_fields,
    },
  } as DashboardItem;
});

const handleDownloadConfirmation = async (orderNo: string) => {
  if (!orderNo) {
    showToast('Order number not available', 'error');
    return;
  }

  isDownloading.value = true;
  try {
    const endpoint = import.meta.env.VITE_ORDER_CONFIRMATION_ENDPOINT;
    const sharedToken = import.meta.env.VITE_ADMIN_DASHBOARD_SHARED_TOKEN;

    // Build URL exactly as Anders specified
    const url = `${endpoint}?order_no=${encodeURIComponent(orderNo)}&authToken=${encodeURIComponent(sharedToken)}`;

    // Call endpoint → receives PDF directly
    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/pdf' },
    });

    if (!response.ok) {
      // Try to parse error JSON, fallback to status text
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `Failed: HTTP ${response.status}`);
    }

    // Trigger browser download
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `OrderConfirmation_${orderNo}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    showToast('Order confirmation downloaded', 'success');
  } catch (error: any) {
    console.error('Download failed:', error);
    showToast('Failed to generate confirmation', 'error');
  } finally {
    isDownloading.value = false;
  }
};

const openPutOnHoldModal = (mode: 'put' | 'resume') => {
  putModalMode.value = mode;
  putModalTitle.value = mode === 'resume' ? 'Resume Order' : 'Put Order On Hold';
  // use order_item id (order item record id) to match backend expectation
  putModalOrderItemId.value = order_item.value?.id ?? (order_item as any).value.id;
  showPutOnHoldModal.value = true;
};

const handlePutOnHoldSuccess = (resData?: any) => {
  showPutOnHoldModal.value = false;
  // refresh parent list / details
  emits('onFetch', true);
};

const handleDeliveryRequest = (resData?: any) => {
  emits('onFetch', true);
};

const resumeOrder = async (orderItemId: number | string) => {
  if (!orderItemId) return;
  resumeLoading.value = true;
  try {
    const payload = { on_hold: false, orderItemId: orderItemId, on_hold_reason: null };
    const response = await http.post(`order/toggle-onhold-status/${orderItemId}`, payload);
    const resData = response.data;
    showToast(resData?.message ?? 'Order resumed successfully', 'success');
    emits('onFetch', true);
  } catch (err) {
    handleResponseException(err);
  } finally {
    resumeLoading.value = false;
  }
};

const updateUrgency = async (status: Urgency, orderId: number | string) => {
  await updateOrderUrgency(status, orderId);
  emits('onFetch', true);
};
const handleCloseInternalNoteModal = () => {
  internalNoteModal.value = false;
  emits('onFetch', true);
};

const generateBarcode = () => {
  if (!order_item.value.order.order_no) {
    showToast('Cannot generate barcode when order number is not set', 'error');
    return;
  }
  showBarcodeModal.value = true;
};

const generateQrCode = () => {
  if (!order_item.value.order.order_no) {
    showToast('Cannot generate QR codes when order number is not set', 'error');
    return;
  }
  showQrModal.value = true;
};

const onFetch = (fetch: boolean) => {
  changeFactoryModal.value = false;
  provideQuoteModal.value = false;
  emits('onFetch', fetch);
};
const deleteOrder = async (orderId: number, orderNo?: string | number): Promise<void> => {
  await DeleteFromStore(orderId, orderNo);
  emits('onFetch', true);
};

const handlePriceBreakDownModalCloseEvent = (fetch_order_items = false) => {
  priceBreakdownModal.value = false;
  if (fetch_order_items) {
    emits('onFetch', fetch);
  }
};

const priceListData = async (orderId: number, orderItemId?: number) => {
  try {
    const orderData = await getOrderDetail(orderId, orderItemId);
    let order_item_total_price = 0; // Use `let` instead of `const`
    const factory_products = orderData.items?.[0]?.factory_products ?? [];
    factory_products.forEach((product) => {
      order_item_total_price += product?.prices?.factory_product_total_price ?? 0;
    });
    order_item.value.factory_products = factory_products;
    order_item.value.order_item_total_price = order_item_total_price;
  } catch (error) {
    logError('Error fetching price list data:', error);
    return null;
  }
};

const changeFactory = async (orderId: number, orderItemId?: number) => {
  await priceListData(orderId, orderItemId);
  changeFactoryModal.value = true;
};

const factory_techpack_urls: ComputedRef<Array<any>> = computed(() => {
  if ((order_item.value as DashboardItem).factory_techpack_pdf_urls) {
    return (order_item.value as DashboardItem).factory_techpack_pdf_urls;
  }
  return [];
});
const merchant_techpack_urls: ComputedRef<Array<any>> = computed(() => {
  if ((order_item.value as DashboardItem).order.merchant_techpack_pdf_urls) {
    return (order_item.value as DashboardItem).order.merchant_techpack_pdf_urls;
  }
  return [];
});

const viewDefaultTechpacks: ComputedRef<boolean> = computed(() => {
  if (can('view-merchant-pdf')) {
    return (
      merchant_techpack_urls.value.every((url) => url === null) ||
      merchant_techpack_urls.value.length === 0
    );
  } else if (can('view-factory-pdf')) {
    return (
      factory_techpack_urls.value.every((url) => url === null) ||
      factory_techpack_urls.value.length === 0
    );
  } else {
    return false;
  }
});
const viewMerchatTechpacks: ComputedRef<boolean> = computed(() => {
  if (can('view-merchant-pdf')) {
    return (
      merchant_techpack_urls.value.length > 0 &&
      merchant_techpack_urls.value.some((url) => url !== null)
    );
  }
  return false;
});
const viewFactoryTechpacks: ComputedRef<boolean> = computed(() => {
  if (can('view-factory-pdf')) {
    return (
      factory_techpack_urls.value.length > 0 &&
      factory_techpack_urls.value.some((url) => url !== null)
    );
  }
  return false;
});
const fetchAndOpenPdf = async (order, version_id) => {
  try {
    let order_id = order.id;
    let techpack_data_length = order.techpack_data ? order.techpack_data.length : 0;
    loading.value = true;
    const response = await http.get(`/techpackpdf/order/${order_id}/version/${version_id}`);
    loading.value = false;
    const pdfUrl = response.data.result.pdf_url;
    if (pdfUrl) {
      window.open(`${storage_url}/${pdfUrl}`, '_blank');
    } else {
      if (techpack_data_length) {
        openV2Application(
          {
            name: '',
            to: `/order/${order_id}/condensend-pdf?techpack_version=${version_id}`,
          },
          true
        );
      } else {
        openV2Application({ name: '', to: `/order/${order_id}/condensend-pdf` }, true);
      }
    }
  } catch (error) {
    logError('Error fetching the PDF URL:', error);
  }
};

const onDownloadClick = (base_path: string | null, url: string, title: string) => {
  if (base_path) {
    downloadFile(url, title);
  } else {
    showToast('File not ready yet. Please try again later', 'info');
  }
};
const downloadFactoryPI = async (order) => {
  if (order.factory_pi_url) {
    loading.value = true;
    await http.get(`order/downloadPerformaInvoice/${order.id}`);

    loading.value = false;
  } else {
    showToast('Factory PI not ready yet. Please try again later', 'info');
  }
};

const handleOrderPriceModal = () => {
  emits('openOrderPriceModal');
};
const IsCustomTechpackUploaded: ComputedRef<boolean> = computed(() => {
  return !!order_item.value.order.custom_techpack_pdf;
});
const handleCustomTechPackModalCloseEvent = () => {
  customTechpackModal.value = false;
};

const copyOrder = async (orderId: number) => {
  try {
    const orderData = await getOrderDetail(orderId);
    openCopyModal.value = true;
    fullOrderDetails.value = orderData;
  } catch (error) {
    logError('Error fetching the order details:', error);
  }
};
function syncOrderItemFromProps(): void {
  const listRow = props.order_item_list as DashboardItem | undefined;
  if (!listRow?.id) return;
  const detailItem = props.order?.items?.find((i) => i.id === listRow.id);
  order_item.value = {
    ...listRow,
    price_info: detailItem?.price_info ?? listRow.price_info,
    currency: detailItem?.currency ?? listRow.currency,
    billing_company_id: listRow.billing_company_id ?? detailItem?.billing_company_id,
    billing_company: detailItem?.billing_company ?? (listRow as any).billing_company,
    factory_products: detailItem?.factory_products ?? listRow.factory_products,
    item_invoices:
      (detailItem as any)?.item_invoices ??
      (detailItem as any)?.itemInvoices ??
      listRow.item_invoices ??
      listRow.itemInvoices,
    invoice_composite_status:
      detailItem?.invoice_composite_status ?? listRow.invoice_composite_status,
    order: {
      ...listRow.order,
      company: props.order?.company ?? listRow.order?.company,
      price_info: props.order?.price_info ?? listRow.order?.price_info,
    },
  } as DashboardItem;
}

watch(() => props.order_item_list, syncOrderItemFromProps, { deep: true });
watch(() => props.order, syncOrderItemFromProps, { deep: true });

const toggleDQC = async (orderId: number, event: Event) => {
  const target = event.target as HTMLInputElement;
  const intendedStatus = target.checked;

  try {
    // Proceed only if confirmed
    target.checked = intendedStatus;
    const payload = {
      dqc_approved: intendedStatus,
      user_id: currentUser.value?.id,
      name: currentUser.value?.name,
    };

    await updateOrderDQCStatus(orderId, payload);
    emits('onFetch', fetch);
  } catch (error) {
    console.error('Error updating DQC status:', error);
    // Revert on error
    target.checked = !intendedStatus;
  } finally {
    // No additional action needed here since we handle state in try/catch
  }
};

const fetchDeliveryRequestDate = async (order_item_id) => {
  try {
    const response = await http.get(`/order/get-delivery-request-date/${order_item_id}`);
    if (response.data && response.data.delivery_request_date) {
      delivery_request_date.value = response.data.delivery_request_date;
      isExistsDeliveryRequestDate.value = true;
    } else {
      isExistsDeliveryRequestDate.value = false;
    }
  } catch (error) {
    delivery_request_date.value = null;
    isExistsDeliveryRequestDate.value = false;
  }
};

const handleEnterDeliveryRequestDate = async (order_item) => {
  await fetchDeliveryRequestDate(order_item.id);
  deliveryRequestDate.value = true;
};
</script>

<style scoped>
.order-actions-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
  padding-bottom: 1rem;
  width: 100%;
  border-bottom: 1px solid #e5e7eb;
  margin-left: auto;
}

.order-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #fff;
  color: #292d32;
  border-radius: 6px;
  padding: 0.3rem 0.5rem;
  border: 1px solid #e5e7eb;
  font-weight: 500;
  font-size: 12px;
  transition:
    background 0.18s,
    color 0.18s,
    box-shadow 0.18s,
    border 0.18s;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(60, 60, 60, 0.04);
}

.assign-admin-button {
  padding: 0;
  border: none;
  box-shadow: none;
  background: white;
}

.order-action-btn:hover,
.order-action-btn:focus {
  background: #e0f7ef;
  color: #00b087;
  border-color: #00b087;
  outline: none;
}

/* A visible focus ring, because `outline: none` above removes the browser's and
   the row is now operated by keyboard as well as mouse (AB#21204). */
.order-action-btn:focus-visible,
.order-actions-menu [role='menuitem']:focus-visible {
  outline: 2px solid #00b087;
  outline-offset: 1px;
}

/* Present but inert — Support Threads on a company without Zendesk. Reads as
   unavailable without being removed from the tab order; the `title` says why. */
.order-action-btn--unavailable {
  color: #9ca3af;
  background: #f7f8f8;
  border-color: #e5e7eb;
  cursor: not-allowed;
  box-shadow: none;
}

.order-action-btn--unavailable:hover,
.order-action-btn--unavailable:focus {
  background: #f7f8f8;
  color: #9ca3af;
  border-color: #e5e7eb;
}

/* The overflow menu. Pushed to the right so it reads as "and the rest" rather
   than as another action in the sequence. */
.order-actions-more {
  margin-left: auto;
}

.order-actions-menu {
  display: flex;
  flex-direction: column;
  min-width: 232px;
  padding: 4px;
}

.order-actions-menu [role='menuitem'],
.order-actions-menu > div,
.order-actions-menu > a,
.order-actions-menu > button {
  width: 100%;
  justify-content: flex-start;
  text-align: left;
  border: none;
  box-shadow: none;
  border-radius: 5px;
  padding: 0.5rem 0.65rem;
}

.order-actions-menu [role='menuitem']:hover {
  background: #f4f4f4;
  color: #292d32;
}

.order-actions-menu-rule {
  border: none;
  border-top: 1px solid #ececec;
  margin: 4px 2px;
}

/* Delete Order keeps its red in the menu. It is the last thing in the list and
   the only one below the rule; losing the colour as well would leave nothing
   separating it from a routine action. */
.order-actions-menu .delete-order-btn {
  color: #d64545;
}

.order-actions-menu .delete-order-btn:hover {
  background: #fdeceb;
  color: #d64545;
}

.order-action-btn .size-5 {
  width: 1.2em;
  height: 1.2em;
}

.urgency-trigger {
  display: flex;
  align-items: center;
  height: 100%;
  gap: 0.5rem;
  width: 100%;
  background: #fff;
  color: #292d32;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.3rem 0.5rem;
  font-weight: 500;
  font-size: 12px;
  transition:
    background 0.18s,
    color 0.18s,
    box-shadow 0.18s,
    border 0.18s;
  cursor: pointer;
  min-width: 120px;
}

.urgency-trigger:hover,
.urgency-trigger:focus {
  background: #e0f7ef;
  color: #00b087;
  border-color: #00b087;
  outline: none;
}

.urgency-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0;
  background: transparent;
  border: none;
}

.urgency-option {
  justify-content: flex-start;
  width: 100%;
  text-align: left;
  box-shadow: none;
  margin: 0;
  background: #fff;
  transition:
    background 0.18s,
    color 0.18s,
    border 0.18s;
  padding: 0.5rem 1.2rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;
  font-weight: 500;
  font-size: 1rem;
}

.urgency-option.vip:hover,
.urgency-option.vip:focus {
  background: #fffbe6;
  color: #d48806;
  border-color: #d48806;
}

.urgency-option.urgent:hover,
.urgency-option.urgent:focus {
  background: #fff0f6;
  color: #c41d7f;
  border-color: #c41d7f;
}

.urgency-option.extra:hover,
.urgency-option.extra:focus {
  background: #e6f7ff;
  color: #1890ff;
  border-color: #1890ff;
}

.urgency-option.remove:hover,
.urgency-option.remove:focus {
  background: #f4f4f4;
  color: #888;
  border-color: #888;
}

.techpack-trigger {
  width: 100%;
  justify-content: flex-start;
}

.techpack-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0;
  background: transparent;
  border: none;
}

.techpack-option {
  width: 100%;
  justify-content: flex-start;
  text-align: left;
  margin: 0;
  background: #fff;
  transition:
    background 0.18s,
    color 0.18s,
    border 0.18s;
  padding: 0.5rem 1.2rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.techpack-option:hover,
.techpack-option:focus {
  background: #e0f7ef;
  color: #00b087;
  border-color: #00b087;
}

.delete-order-btn {
  color: #f15058;
  border-color: #f15058;
  transition:
    background 0.18s,
    color 0.18s,
    border 0.18s;
}

.delete-order-btn:hover,
.delete-order-btn:focus {
  background: #f15058;
  color: #fff;
  border-color: #f15058;
}
</style>
